import { useState, useEffect, useRef, useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { Search, Edit3, Trash2, Mic, QrCode } from 'lucide-react'
import { apiClient } from '../services/api'
import { ui } from '../styles/ui'
import Button from '../components/ui/Button'
import ConfirmDialog from '../components/ui/ConfirmDialog'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import { useTranslation } from '../contexts/TranslationContext'
import { formatEmptyValue } from '../utils/displayValue'

export default function ReceiptProducts() {
  const PRODUCTS_PAGE_SIZE = 25
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState(searchParams.get('q') || '')
  const [receiptFilter, setReceiptFilter] = useState(searchParams.get('receipt_id') || '')
  const [sortBy, setSortBy] = useState('id')
  const [sortOrder, setSortOrder] = useState('desc')
  const { t } = useTranslation()
  const [products, setProducts] = useState([])
  const [receipts, setReceipts] = useState([])
  const [receiptImages, setReceiptImages] = useState({})
  const [receiptImageErrors, setReceiptImageErrors] = useState({})
  const receiptImageFetchInFlight = useRef(new Set())
  const receiptById = useMemo(
    () => new Map(receipts.map((receipt) => [receipt.id, receipt])),
    [receipts],
  )
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [page, setPage] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const sentinelRef = useRef(null)
  const [receiptId, setReceiptId] = useState('')
  const [categoryId, setCategoryId] = useState('')
  const [name, setName] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [unitPrice, setUnitPrice] = useState(0)
  const [lineTotal, setLineTotal] = useState(0)
  const [categories, setCategories] = useState([])
  const [editingProduct, setEditingProduct] = useState(null)
  const [formError, setFormError] = useState('')
  const [confirmDeleteProduct, setConfirmDeleteProduct] = useState(null)
  const [selectedProductIds, setSelectedProductIds] = useState([])
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)

  const fetchProducts = async (q = '', p = 1, append = false) => {
    try {
      if (append) {
        setLoadingMore(true)
      } else {
        setLoading(true)
      }
      const params = { q, page: p, per_page: PRODUCTS_PAGE_SIZE, receipt_id: receiptFilter || undefined, sort_by: sortBy || undefined, sort_order: sortOrder || undefined }
      const response = await apiClient.get('/receipt-products', { params })
      const nextProducts = response.data.data || []
      setProducts((prev) => (append ? [...prev, ...nextProducts] : nextProducts))
      const currentPage = response.data.current_page || 1
      const lastPage = response.data.last_page || 1
      const explicitHasMore = response.data.hasMore
      setPage(currentPage)
      setHasMore(
        typeof explicitHasMore === 'boolean'
          ? explicitHasMore
          : currentPage < lastPage,
      )
      if (!append) {
        setSelectedProductIds([])
      }
    } catch (err) {
      console.error('Failed to fetch products', err)
    } finally {
      setLoading(false)
      setLoadingMore(false)
    }
  }

  const fetchReceipts = async () => {
    try {
      const response = await apiClient.get('/receipts')
      setReceipts(response.data.data || response.data || [])
    } catch (err) {
      console.error('Failed to fetch receipts', err)
    }
  }

  const parseSourceData = (sourceData) => {
    if (!sourceData) return null
    if (typeof sourceData === 'object') return sourceData
    if (typeof sourceData === 'string') {
      try {
        const parsed = JSON.parse(sourceData)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch {
        return null
      }
    }
    return null
  }

  const getInlineImagePreview = (receipt) => {
    const sourceData = parseSourceData(receipt?.source_data ?? receipt?.sourceData)
    if (!sourceData) return null
    return (
      sourceData.base64 || sourceData.url || sourceData.image_url || sourceData.imageUrl || null
    )
  }

  const arrayBufferToBase64 = (buffer) => {
    let binary = ''
    const bytes = new Uint8Array(buffer)
    for (let i = 0; i < bytes.byteLength; i += 1) {
      binary += String.fromCharCode(bytes[i])
    }
    return window.btoa(binary)
  }

  const fetchReceiptImage = async (receiptId) => {
    if (!receiptId || receiptImageFetchInFlight.current.has(receiptId)) return
    receiptImageFetchInFlight.current.add(receiptId)
    try {
      const response = await apiClient.get(`/receipts/${receiptId}/image`, { responseType: 'arraybuffer' })
      const base64Data = arrayBufferToBase64(response.data)
      const contentType = response?.headers?.['content-type'] || 'image/jpeg'
      setReceiptImages((prev) => ({
        ...prev,
        [receiptId]: `data:${contentType};base64,${base64Data}`,
      }))
    } catch (err) {
      setReceiptImageErrors((prev) => ({ ...prev, [receiptId]: true }))
    } finally {
      receiptImageFetchInFlight.current.delete(receiptId)
    }
  }

  useEffect(() => {
    receipts.forEach((receipt) => {
      const sourceType = String(receipt?.source_type || '').toLowerCase()
      if (sourceType !== 'image') return
      if (getInlineImagePreview(receipt)) return
      if (receiptImages[receipt.id]) return
      if (receiptImageErrors[receipt.id]) return
      fetchReceiptImage(receipt.id)
    })
  }, [receipts, receiptImages, receiptImageErrors])

  const renderReceiptPreview = (receipt, receiptId) => {
    if (!receipt) {
      return (
        <span className="text-muted-foreground">{receiptId ? `#${receiptId}` : formatEmptyValue(null)}</span>
      )
    }

    const sourceType = String(receipt?.source_type || '').toLowerCase()

    if (sourceType === 'image') {
      const imageSrc = getInlineImagePreview(receipt) || receiptImages[receipt.id]
      if (imageSrc) {
        return (
          <img
            src={imageSrc}
            alt={`Receipt #${receipt.id} preview`}
            className="h-12 w-12 rounded-md border border-outline object-cover"
          />
        )
      }
      if (receiptImageErrors[receipt.id]) {
        return <span className="text-xs text-muted-foreground">No image</span>
      }
      return <span className="text-xs text-muted-foreground">Loading...</span>
    }

    if (sourceType === 'qrcode') {
      return (
        <span
          aria-label="QR receipt source"
          title="QR receipt source"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-sky-200 bg-sky-50 text-sky-700"
        >
          <QrCode size={20} />
        </span>
      )
    }

    if (sourceType === 'speech') {
      return (
        <span
          aria-label="Speech receipt source"
          title="Speech receipt source"
          className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-emerald-50 text-emerald-700"
        >
          <Mic size={20} />
        </span>
      )
    }

    return <span className="text-muted-foreground">{formatEmptyValue(null)}</span>
  }

  function toggleSort(field) {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(field)
      setSortOrder('asc')
    }
    setPage(1)
  }

  function sortMarker(field) {
    if (sortBy !== field) return ''
    return sortOrder === 'asc' ? ' в†‘' : ' в†“'
  }

  useEffect(() => {
    fetchProducts()
    fetchCategories()
    fetchReceipts()
  }, [sortBy, sortOrder, receiptFilter])

  const handleSearch = async (e) => {
    e.preventDefault()
    setHasMore(false)
    setPage(1)
    await fetchProducts(query, 1, false)
  }

  const fetchCategories = async () => {
    try {
      const response = await apiClient.get('/categories', { params: { type: 'product' } })
      setCategories((response.data?.data ?? response.data) || [])
    } catch (err) {
      console.error('Failed to fetch categories', err)
    }
  }

  const resetForm = () => {
    setReceiptId('')
    setCategoryId('')
    setName('')
    setQuantity(1)
    setUnitPrice(0)
    setLineTotal(0)
    setEditingProduct(null)
    setFormError('')
  }

  const getProductQty = (product) => product.qty ?? 0
  const getProductPrice = (product) => Number(product.price ?? product.unit_price ?? 0)
  const getProductCategoryId = (product) => {
    // WHY: receipt sources are formatted by different backend paths; edit must match view data.
    const categoryId = product?.product_category_id ?? product?.category_id ?? product?.category?.id ?? ''
    return categoryId ? categoryId.toString() : ''
  }

  const fillFormFromProduct = (product) => {
    setReceiptId(product.receipt_id?.toString() || '')
    setCategoryId(getProductCategoryId(product))
    setName(product.name || '')
    setQuantity(getProductQty(product) || 1)
    setUnitPrice(getProductPrice(product))
    setLineTotal(getProductQty(product) * getProductPrice(product))
    setEditingProduct(product)
    setFormError('')
  }

  const submitForm = async (e) => {
    e.preventDefault()
    if (!receiptId || !name) {
      setFormError(t('Receipt and Name are required.', 'Receipt and Name are required.'))
      return
    }

    const payload = {
      name,
      product_category_id: categoryId || null,
      qty: Number(quantity),
      price: Number(unitPrice),
    }

    try {
      setLoading(true)
      if (editingProduct?.id) {
        await apiClient.put(`/receipt-products/${editingProduct.id}`, payload)
      } else {
        await apiClient.post(`/receipts/${receiptId}/products`, payload)
      }
      await fetchProducts(query, page)
      closeEditModal()
    } catch (err) {
      console.error('Failed to save product', err)
      setFormError(t('Failed to save product. Please try again.', 'Failed to save product. Please try again.'))
    } finally {
      setLoading(false)
    }
  }

  const deleteProduct = (id) => {
    setConfirmDeleteProduct(id)
    setConfirmModalOpen(true)
  }

  const openCreateModal = () => {
    resetForm()
    setEditModalOpen(true)
  }

  const openEditModal = (product) => {
    fillFormFromProduct(product)
    setEditModalOpen(true)
  }

  const closeEditModal = () => {
    setEditModalOpen(false)
    setEditingProduct(null)
    resetForm()
  }

  const toggleSelectProduct = (id) => {
    setSelectedProductIds((prev) =>
      prev.includes(id) ? prev.filter((selected) => selected !== id) : [...prev, id]
    )
  }

  const toggleSelectAllProducts = () => {
    if (selectedProductIds.length === products.length) {
      setSelectedProductIds([])
      return
    }

    setSelectedProductIds(products.map((product) => product.id))
  }

  const handleBulkDelete = () => {
    if (!selectedProductIds.length) return
    setConfirmDeleteProduct('bulk')
    setConfirmModalOpen(true)
  }

  const confirmDelete = async () => {
    if (!confirmDeleteProduct) return

    try {
      setLoading(true)
      if (confirmDeleteProduct === 'bulk') {
        await apiClient.delete('/receipt-products', { data: { ids: selectedProductIds } })
        setSelectedProductIds([])
      } else {
        await apiClient.delete(`/receipt-products/${confirmDeleteProduct}`)
      }

      const nextPage = products.length === 1 && page > 1 ? page - 1 : page
      await fetchProducts(query, nextPage, false)
      setConfirmDeleteProduct(null)
      setConfirmModalOpen(false)
    } catch (err) {
      console.error('Failed to delete product', err)
    } finally {
      setLoading(false)
    }
  }

  const cancelDelete = () => {
    setConfirmDeleteProduct(null)
    setConfirmModalOpen(false)
  }

  const updateLineTotal = (qty, price) => {
    const q = Number(qty) || 0
    const p = Number(price) || 0
    setLineTotal(q * p)
  }

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !sentinelRef.current || !hasMore || loadingMore || loading) return
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          fetchProducts(query, page + 1, true)
        }
      },
      { rootMargin: '200px' },
    )
    observer.observe(sentinelRef.current)
    return () => observer.disconnect()
  }, [hasMore, loadingMore, loading, page, query])

  return (
    <PageShell>
      <PageHeader title={t('Receipt Products', 'Receipt Products')} />

      <form onSubmit={handleSearch} className="mb-4 flex gap-2">
        <input
          className={`flex-1 ${ui.input}`}
          placeholder={t('Search products by name or other fields', 'Search products by name or other fields')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <Button type="submit" variant="primary">
          <Search size={16} />
          {t('Search', 'Search')}
        </Button>
      </form>

      {editingProduct && (
        <div className="mb-6 text-sm text-foreground">
          {t('Editing product #', 'Editing product #')}{editingProduct.id}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className={ui.spinnerLg}></div>
        </div>
      ) : (
        <div>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-outline bg-surface-elevated p-4 text-sm text-foreground">
            <div className="flex flex-wrap items-center gap-3">
              {receiptFilter && (
                <span className="inline-flex items-center gap-1 rounded-md bg-blue-100 px-2 py-1 text-xs font-medium text-blue-800">
                  Receipt: {receiptFilter.slice(0, 8)}...
                  <button onClick={() => { setReceiptFilter(''); setPage(1); }} className="ml-1 text-blue-600 hover:text-blue-800">&times;</button>
                </span>
              )}
              {selectedProductIds.length > 0 && (
                <span>{selectedProductIds.length} {selectedProductIds.length === 1 ? t('product selected', 'product selected') : t('products selected', 'products selected')}</span>
              )}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={openCreateModal}
                className={ui.buttonSuccess}
              >
                {t('Create Product', 'Create Product')}
              </button>
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={!selectedProductIds.length}
                className={ui.buttonDanger}
              >
                {t('Delete selected', 'Delete selected')}
              </button>
            </div>
          </div>

          <div className={ui.card}>
          <table className={ui.table}>
            <thead className={ui.tableHead}>
              <tr>
                <th className={ui.tableHeadCell}>
                  <input
                    type="checkbox"
                    checked={selectedProductIds.length === products.length && products.length > 0}
                    onChange={toggleSelectAllProducts}
                  />
                </th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('receipt_id')}>Receipt{sortMarker('receipt_id')}</th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('name')}>Name{sortMarker('name')}</th>
                <th className={ui.tableHeadCell}>Category</th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('qty')}>Qty{sortMarker('qty')}</th>
                <th className={`${ui.tableHeadCell} cursor-pointer`} onClick={() => toggleSort('price')}>Unit Price{sortMarker('price')}</th>
                <th className={ui.tableHeadCell}>Line Total</th>
                <th className={ui.tableHeadCell}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-4 py-6 text-center text-muted-foreground">{t('No products found.', 'No products found.')}</td>
                </tr>
              ) : (
                products.map((product) => (
                  <tr key={product.id} className="bg-surface hover:bg-surface-elevated">
                    <td className={ui.tableCell}>
                      <input
                        type="checkbox"
                        checked={selectedProductIds.includes(product.id)}
                        onChange={() => toggleSelectProduct(product.id)}
                      />
                    </td>
                    <td className={ui.tableCellText}>
                      <Link to={`/receipts/${product.receipt_id}`} className="inline-flex items-center">
                        {renderReceiptPreview(receiptById.get(product.receipt_id), product.receipt_id)}
                      </Link>
                    </td>
                    <td className={ui.tableCellText}>{product.name}</td>
                    <td className={ui.tableCellText}>{formatEmptyValue(product.category?.name)}</td>
                    <td className={ui.tableCellText}>{getProductQty(product)}</td>
                    <td className={ui.tableCellText}>{getProductPrice(product).toFixed(2)}</td>
                    <td className={ui.tableCellText}>{Number(product.line_total ?? (getProductQty(product) * getProductPrice(product))).toFixed(2)}</td>
                    <td className={`${ui.tableCell} whitespace-nowrap space-x-2`}>
                      <button
                        onClick={() => openEditModal(product)}
                        className={ui.buttonSmPrimary}
                      >
                        <Edit3 size={14} /> {t('Edit', 'Edit')}
                      </button>
                      <button
                        onClick={() => deleteProduct(product.id)}
                        className={ui.buttonSmDanger}
                      >
                        <Trash2 size={14} /> {t('Delete', 'Delete')}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {editModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl rounded-2xl bg-surface p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold">{editingProduct ? t('Edit Product', 'Edit Product') : t('Create Product', 'Create Product')}</h2>
              <button
                type="button"
                onClick={closeEditModal}
                className={ui.buttonGhost}
              >
                {t('Close', 'Close')}
              </button>
            </div>
            <form onSubmit={submitForm} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Receipt', 'Receipt')}</label>
                  <select
                    value={receiptId}
                    onChange={(e) => setReceiptId(e.target.value)}
                    className={ui.select}
                    disabled={!!editingProduct}
                  >
                    <option value="">{t('Select a receipt', 'Select a receipt')}</option>
                    {receipts.map((receipt) => (
                      <option key={receipt.id} value={receipt.id}>
                        {receipt.id} {receipt.file_name ? `- ${receipt.file_name}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Name', 'Name')}</label>
                  <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className={ui.input}
                    placeholder={t('Product name', 'Product name')}
                  />
                </div>
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Category', 'Category')}</label>
                  <select
                    value={categoryId}
                    onChange={(e) => setCategoryId(e.target.value)}
                    className={ui.select}
                  >
                    <option value="">{t('Select category', 'Select category')}</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>{category.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Quantity', 'Quantity')}</label>
                  <input
                    type="number"
                    value={quantity}
                    min={0}
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      setQuantity(val)
                      updateLineTotal(val, unitPrice)
                    }}
                    className={ui.input}
                  />
                </div>
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Unit Price', 'Unit Price')}</label>
                  <input
                    type="number"
                    value={unitPrice}
                    min={0}
                    step="0.01"
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      setUnitPrice(val)
                      updateLineTotal(quantity, val)
                    }}
                    className={ui.input}
                  />
                </div>
                <div>
                  <label className={`${ui.label} mb-1`}>{t('Line Total', 'Line Total')}</label>
                  <input
                    type="number"
                    value={lineTotal}
                    min={0}
                    step="0.01"
                    onChange={(e) => setLineTotal(Number(e.target.value))}
                    className={ui.input}
                  />
                </div>
              </div>
              {formError && <p className="text-sm text-red-600">{formError}</p>}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className={ui.buttonGhost}
                  disabled={loading}
                >
                  {t('Cancel', 'Cancel')}
                </button>
                <button
                  type="submit"
                  className={ui.buttonPrimary}
                  disabled={loading}
                >
                  {t('Update Product', 'Update Product')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {hasMore && !loading && (
        <div className="mt-4 flex justify-center">
          <button
            type="button"
            className={ui.buttonPrimary}
            onClick={() => fetchProducts(query, page + 1, true)}
            disabled={loadingMore}
          >
            {loadingMore ? t('Loading more...', 'Loading more...') : t('Load more', 'Load more')}
          </button>
        </div>
      )}
      <div ref={sentinelRef} className="h-1" />

      <ConfirmDialog
        open={confirmModalOpen}
        title={t('Delete Product', 'Delete Product')}
        message={t('Are you sure you want to delete this product? This action cannot be undone.', 'Are you sure you want to delete this product? This action cannot be undone.')}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        confirmText={t('Delete', 'Delete')}
        cancelText={t('Cancel', 'Cancel')}
        loading={loading}
      />
    </PageShell>
  )
}

